// login-frontend/src/components/PetSim/Pet.jsx
import React, { useEffect, useState } from "react";
import "./Pet.css";

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    Button,
    Divider,
    Chip,
} from "@mui/material";

import avatarMaleD from "../../assets/petSim/character-male-d.png";
import avatarMaleE from "../../assets/petSim/character-male-e.png";

const AVATAR_OPTIONS = [
    { id: "male-d", label: "Male D", src: avatarMaleD },
    { id: "male-e", label: "Male E", src: avatarMaleE },
];

export default function Pet() {
    const API =
        (typeof window !== "undefined" && window.__ENV?.API_BASE) ||
        import.meta.env.VITE_API_BASE ||
        "";
    const [form, setForm] = useState({
        name: "Buddy",
        species: "dog",
        temperament: "playful",
        difficulty: "normal",
        avatar: "male-d",
    });
    const [pet, setPet] = useState(null);
    const [loading, setLoading] = useState(true);

    // Load current pet (if any)
    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/api/pet/me`, { credentials: "include" });
                const data = await r.json();
                setPet(data);
                if (data?.avatar) setForm((f) => ({ ...f, avatar: data.avatar }));
                if (data?.name) setForm((f) => ({ ...f, name: data.name }));
                if (data?.species) setForm((f) => ({ ...f, species: data.species }));
                if (data?.temperament) setForm((f) => ({ ...f, temperament: data.temperament }));
                if (data?.difficulty) setForm((f) => ({ ...f, difficulty: data.difficulty }));
            } finally {
                setLoading(false);
            }
        })();
    }, [API]);

    // Save → redirect to PetSim on success
    const handleSave = async (e) => {
        e?.preventDefault?.();
        const r = await fetch(`${API}/api/pet`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(form),
        });
        const data = await r.json();
        if (!r.ok) {
            alert(data?.error || "Failed");
            return;
        }
        setPet(data);
        // Jump to the game page
        window.location.href = "/Dashboard/PetSim";
    };

    const handleDelete = async () => {
        if (!confirm("Delete pet? This cannot be undone.")) return;
        const r = await fetch(`${API}/api/pet`, { method: "DELETE", credentials: "include" });
        if (r.status === 204) {
            setPet(null);
            setForm({ name: "Buddy", species: "dog", temperament: "playful", difficulty: "normal", avatar: "male-d" });
            return;
        }
        const data = await r.json().catch(() => ({}));
        alert(data?.error || "Delete failed");
    };

    return (
        <div className="pet-container">
            <Box
                sx={{
                    px: { xs: 2, md: 3 },
                    py: { xs: 2, md: 3 },
                    minHeight: "calc(100dvh - 120px)",
                    display: "grid",
                    placeItems: "start center",
                    background:
                        "radial-gradient(1200px 600px at 10% -10%, rgba(99,230,190,.08), transparent), radial-gradient(1200px 600px at 110% 110%, rgba(122,167,255,.08), transparent)",
                }}
            >
                <Card
                    elevation={8}
                    sx={{
                        width: "min(980px, 96vw)",
                        borderRadius: 3,
                        bgcolor: "rgba(20,20,24,0.9)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        color: "#fff",
                        backdropFilter: "blur(6px)",
                    }}
                >
                    <CardHeader
                        title={
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 0.3 }}>
                                    PetSim
                                </Typography>
                                <Chip size="small" label="Manage" sx={{ bgcolor: "rgba(255,255,255,0.08)", color: "#ddd" }} />
                            </Stack>
                        }
                        action={
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    onClick={() => (window.location.href = "/Dashboard/PetSim")}
                                    sx={{ borderColor: "rgba(255,255,255,0.25)", color: "#ddd" }}
                                >
                                    Play ▶
                                </Button>
                            </Stack>
                        }
                        sx={{ pb: 0.5 }}
                    />

                    <CardContent sx={{ pt: 1.5 }}>
                        {/* Form */}
                        <Box component="form" onSubmit={handleSave}>
                            <Stack spacing={2}>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={2}
                                    sx={{ "& .MuiFormControl-root": { flex: 1 } }}
                                >
                                    <TextField
                                        label="Name"
                                        value={form.name}
                                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                        InputLabelProps={{ sx: { color: "#bbb" } }}
                                        InputProps={{ sx: { color: "#fff" } }}
                                        inputProps={{ maxLength: 64 }}
                                        sx={{ bgcolor: "rgba(255,255,255,0.05)" }}
                                    />
                                    <FormControl sx={{ bgcolor: "rgba(255,255,255,0.05)" }}>
                                        <InputLabel sx={{ color: "#bbb" }}>Species</InputLabel>
                                        <Select
                                            label="Species"
                                            value={form.species}
                                            onChange={(e) => setForm((f) => ({ ...f, species: e.target.value }))}
                                            sx={{
                                                color: "#fff",
                                                ".MuiSvgIcon-root": { color: "#fff" },
                                            }}
                                        >
                                            <MenuItem value="dog">Dog</MenuItem>
                                            <MenuItem value="bear">Bear</MenuItem>
                                            <MenuItem value="chicken">Chicken</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ bgcolor: "rgba(255,255,255,0.05)" }}>
                                        <InputLabel sx={{ color: "#bbb" }}>Temperament</InputLabel>
                                        <Select
                                            label="Temperament"
                                            value={form.temperament}
                                            onChange={(e) => setForm((f) => ({ ...f, temperament: e.target.value }))}
                                            sx={{
                                                color: "#fff",                        
                                                ".MuiSvgIcon-root": { color: "#fff" }, 
                                            }}
                                        >
                                            <MenuItem value="playful">Playful</MenuItem>
                                            <MenuItem value="calm">Calm</MenuItem>
                                            <MenuItem value="aggressive">Aggressive</MenuItem>
                                            <MenuItem value="lazy">Lazy</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ bgcolor: "rgba(255,255,255,0.05)" }}>
                                        <InputLabel sx={{ color: "#bbb" }}>Difficulty</InputLabel>
                                        <Select
                                            label="Difficulty"
                                            value={form.difficulty}
                                            onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}
                                            sx={{
                                                color: "#fff",                       
                                                ".MuiSvgIcon-root": { color: "#fff" }, 
                                            }}
                                        >
                                            <MenuItem value="easy">Easy</MenuItem>
                                            <MenuItem value="normal">Normal</MenuItem>
                                            <MenuItem value="hard">Hard</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

                                {/* Avatar picker */}
                                <Box>
                                    <Typography sx={{ fontWeight: 700, mb: 1 }}>Choose Your Avatar</Typography>
                                    <div className="avatar-grid">
                                        {AVATAR_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.id}
                                                type="button"
                                                className={`avatar-pick ${form.avatar === opt.id ? "selected" : ""}`}
                                                onClick={() => setForm((f) => ({ ...f, avatar: opt.id }))}
                                                title={opt.label}
                                            >
                                                <img src={opt.src} alt={opt.label} />
                                            </button>
                                        ))}
                                    </div>
                                    <Typography className="notice" sx={{ mt: 0.75 }}>
                                        More avatars coming soon. 
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
                                    <Button variant="contained" color="success" type="submit">
                                        Save Pet
                                    </Button>
                                    {pet && (
                                        <Button variant="outlined" color="error" onClick={handleDelete}>
                                            Delete Pet
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </div>
    );
}





